import { graphql } from '../../gql';
import { useApiMutation, useApiQuery } from '../../query/hooks';
import { Typography } from '@mui/material';
import { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { z } from 'zod';
import { ModalContent } from '../../modal/components/ModalContent';
import { ModalActions } from '../../modal/components/ModalActions';
import { useModalContext } from '../../modal/ModalContext';
import { useNavigate } from 'react-router-dom';
import { mutationResponseHandler } from '../../utils/mutationResponseHandler';
import { useSnackbar } from '../../store/snackbar';

const opportunityDeleteModal_getOpportunity = graphql(`
	query opportunityDeleteModal_getOpportunity($id: UUID!) {
		getOpportunity(by: { id: $id }) {
			name
		}
	}
`);

const opportunityDeleteModal_deleteOpportunity = graphql(`
	mutation opportunityDeleteModal_deleteOpportunity($id: UUID!) {
		deleteOpportunity(by: { id: $id }) {
			ok
			errorMessage
		}
	}
`);

export const deleteOpportunityModalSchema = z.object({
	id: z.string(),
});

export const DeleteOpportunityModal: FC<z.infer<typeof deleteOpportunityModalSchema>> = ({ id }) => {
	const { mutate: deleteOpportunity } = useApiMutation(opportunityDeleteModal_deleteOpportunity);
	const { data } = useApiQuery(opportunityDeleteModal_getOpportunity, { variables: { id } });
	const { closeModal } = useModalContext();
	const intl = useIntl();
	const navigate = useNavigate();
	const { openSnackbar } = useSnackbar();

	const handleDelete = () => {
		deleteOpportunity(
			{ id },
			{
				onSuccess: (result) => {
					mutationResponseHandler({
						result: result.deleteOpportunity.ok,
						toastMessages: {
							success: intl.formatMessage({ id: 'opportunity.delete.success' }),
							error: intl.formatMessage({ id: 'opportunity.delete.error' }),
						},
						errorMessage: result.deleteOpportunity.errorMessage,
						callback: () => {
							closeModal();
							navigate('/opportunities');
						},
						openSnackbar,
					});
				},
			},
		);
	};

	return (
		<>
			<ModalContent
				isDelete
				title={intl.formatMessage({ id: 'opportunity.delete.confirmation' }, { name: data?.getOpportunity?.name })}
				description={''}
			>
				<Typography textAlign={'center'}>
					<strong>
						<FormattedMessage id={'common.delete.confirmation'} />
					</strong>
				</Typography>
			</ModalContent>
			<ModalActions
				isDelete
				handleCancelButtonClick={closeModal}
				handlePrimaryButtonClick={handleDelete}
				cancelButtonText={intl.formatMessage({ id: 'common.cancel' })}
				primaryButtonText={intl.formatMessage({ id: 'common.delete' })}
			/>
		</>
	);
};
